/**
 * Access Assignment Panel
 * 
 * Slide-over panel for managing user access:
 * - Outlet scope assignment (MANDATORY for operational users)
 * - Department scope assignment
 * - Module access toggles
 * - Capability overrides
 * - Employee linking (future)
 * 
 * Designed to be opened from the Users page when clicking on a user.
 */

import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  X, Shield, Building2, Briefcase, Users, Check, AlertTriangle,
  ChevronDown, ChevronRight, Loader2, Info, ToggleLeft, ToggleRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// View scope options with descriptions
const VIEW_SCOPES = [
  { value: 'self', label: 'Self Only', description: 'Can only see own data' },
  { value: 'team', label: 'Team', description: 'Can see direct reports' },
  { value: 'department', label: 'Department', description: 'Can see entire department' },
  { value: 'outlet', label: 'Outlet', description: 'Can see entire outlet' },
  { value: 'org', label: 'Organization', description: 'Can see entire organization' },
];

// Reporting mode options
const REPORTING_MODES = [
  { value: 'none', label: 'None', description: 'No reporting access' },
  { value: 'direct_reports', label: 'Direct Reports', description: 'Only direct reports' },
  { value: 'department', label: 'Department', description: 'All in department' },
  { value: 'custom', label: 'Custom', description: 'Custom employee list' },
];

// Application access status, exactly as the backend resolver reports it.
// No module list lives in the frontend: the catalogue comes from the canonical
// app registry via GET /orgs/{org}/users/{user}/applications.
const ACCESS_STATES = {
  ACTIVE: { label: 'Active', className: 'bg-green-100 text-green-700' },
  BLOCKED: { label: 'Blocked', className: 'bg-red-100 text-red-700' },
  NO_ROLE_ACCESS: { label: 'No role access', className: 'bg-gray-200 text-gray-600' },
  INACTIVE_ORG_DISABLED: { label: 'Inactive — organisation disabled', className: 'bg-amber-100 text-amber-800' },
  USER_INACTIVE: { label: 'Account deactivated', className: 'bg-gray-200 text-gray-600' },
};

const AccessAssignmentPanel = ({ user, isOpen, onClose, onSave }) => {
  const { user: currentUser } = useAuth();
  const orgId = user?.org_id || currentUser?.org_id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [outlets, setOutlets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [scopeAssignment, setScopeAssignment] = useState(null);
  const [applications, setApplications] = useState([]);
  const [togglingApp, setTogglingApp] = useState(null);
  const [resolvedAccess, setResolvedAccess] = useState(null);
  
  // Form states
  const [selectedOutlets, setSelectedOutlets] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [viewScope, setViewScope] = useState('self');
  const [reportingMode, setReportingMode] = useState('none');
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    scope: true,
    modules: true,
    capabilities: false,
  });

  useEffect(() => {
    if (isOpen && user) {
      loadAccessData();
    }
  }, [isOpen, user]);

  const loadAccessData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load all data in parallel
      const [outletsRes, deptsRes, scopeRes, appsRes, resolvedRes] = await Promise.allSettled([
        api.getOutlets(),
        api.getDepartments(),
        api.getUserScope(user.user_id),
        orgId ? api.getUserApplications(orgId, user.user_id) : Promise.reject(new Error('no org')),
        api.getResolvedAccess(user.user_id, 'people_engine'),
      ]);
      
      // Set outlets (handle axios response)
      if (outletsRes.status === 'fulfilled') {
        const outletsData = outletsRes.value?.data || outletsRes.value;
        setOutlets(Array.isArray(outletsData) ? outletsData : []);
      }
      
      // Set departments (handle axios response)
      if (deptsRes.status === 'fulfilled') {
        const deptsData = deptsRes.value?.data || deptsRes.value;
        setDepartments(Array.isArray(deptsData) ? deptsData : []);
      }
      
      // Set scope assignment (handle axios response)
      if (scopeRes.status === 'fulfilled' && scopeRes.value) {
        const scope = scopeRes.value?.data || scopeRes.value;
        setScopeAssignment(scope);
        setSelectedOutlets(scope.outlet_ids || []);
        setSelectedDepartments(scope.department_ids || []);
        setViewScope(scope.view_scope || 'self');
        setReportingMode(scope.reporting_mode || 'none');
      }
      
      // Application access — resolved entirely by the backend
      if (appsRes.status === 'fulfilled') {
        const data = appsRes.value?.data || appsRes.value;
        setApplications(Array.isArray(data?.applications) ? data.applications : []);
      } else {
        setApplications([]);
      }
      
      // Set resolved access (for capability inspection)
      if (resolvedRes.status === 'fulfilled') {
        const resolvedData = resolvedRes.value?.data || resolvedRes.value;
        setResolvedAccess(resolvedData);
      }
      
    } catch (err) {
      console.error('Error loading access data:', err);
      setError('Failed to load access data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScope = async () => {
    setSaving(true);
    setError(null);
    
    try {
      await api.updateUserScope(user.user_id, {
        outlet_ids: selectedOutlets,
        department_ids: selectedDepartments,
        view_scope: viewScope,
        reporting_mode: reportingMode,
      });
      
      // Refresh data
      await loadAccessData();
      
      if (onSave) onSave();
    } catch (err) {
      console.error('Error saving scope:', err);
      setError(err.response?.data?.detail || 'Failed to save scope assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleAppToggle = async (app, enabled) => {
    setTogglingApp(app.app_code);
    setError(null);
    try {
      await api.setOrgApplicationUserAccess(orgId, app.app_code, user.user_id, enabled);
      await loadAccessData();
      if (onSave) onSave();
    } catch (err) {
      console.error('Error toggling application access:', err);
      setError(err.response?.data?.detail?.detail || err.response?.data?.detail || 'Failed to update application access');
    } finally {
      setTogglingApp(null);
    }
  };

  const toggleOutlet = (outletId) => {
    setSelectedOutlets(prev => 
      prev.includes(outletId) 
        ? prev.filter(id => id !== outletId)
        : [...prev, outletId]
    );
  };

  const toggleDepartment = (deptId) => {
    setSelectedDepartments(prev => 
      prev.includes(deptId) 
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
            data-testid="access-panel-backdrop"
          />
          
          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 overflow-y-auto"
            data-testid="access-assignment-panel"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Access Assignment</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {user?.user_name || user?.user_email}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="close-access-panel"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertTriangle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : (
                <>
                  {/* Migration Notice */}
                  {scopeAssignment?.source === 'migration_default' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <Info size={18} className="text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <strong>Migration Default:</strong> This user was auto-assigned to all outlets during migration. 
                        Review and adjust their scope as needed.
                      </div>
                    </div>
                  )}

                  {/* Outlet Scope Section (CRITICAL) */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('scope')}
                      className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 size={18} className="text-blue-600" />
                        <span className="font-medium">Outlet & Department Scope</span>
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">REQUIRED</span>
                      </div>
                      {expandedSections.scope ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    
                    {expandedSections.scope && (
                      <div className="p-4 space-y-4">
                        {/* Outlet Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assigned Outlets
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <div className="space-y-2">
                            {outlets.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">No outlets available</p>
                            ) : (
                              outlets.map(outlet => (
                                <label 
                                  key={outlet.outlet_id}
                                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedOutlets.includes(outlet.outlet_id)}
                                    onChange={() => toggleOutlet(outlet.outlet_id)}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    data-testid={`outlet-checkbox-${outlet.outlet_id}`}
                                  />
                                  <div>
                                    <span className="text-sm font-medium">{outlet.outlet_name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({outlet.outlet_code})</span>
                                  </div>
                                </label>
                              ))
                            )}
                          </div>
                          {selectedOutlets.length === 0 && (
                            <p className="text-xs text-red-600 mt-1">
                              At least one outlet must be assigned for operational access
                            </p>
                          )}
                        </div>

                        {/* Department Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assigned Departments
                            <span className="text-gray-400 ml-1">(optional filter)</span>
                          </label>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {departments.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">No departments created yet</p>
                            ) : (
                              departments.map(dept => (
                                <label 
                                  key={dept.department_id}
                                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedDepartments.includes(dept.department_id)}
                                    onChange={() => toggleDepartment(dept.department_id)}
                                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                  />
                                  <div>
                                    <span className="text-sm font-medium">{dept.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({dept.code})</span>
                                  </div>
                                </label>
                              ))
                            )}
                          </div>
                        </div>

                        {/* View Scope */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            View Scope
                          </label>
                          <select
                            value={viewScope}
                            onChange={(e) => setViewScope(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            data-testid="view-scope-select"
                          >
                            {VIEW_SCOPES.map(scope => (
                              <option key={scope.value} value={scope.value}>
                                {scope.label} - {scope.description}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Reporting Mode */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reporting Mode
                          </label>
                          <select
                            value={reportingMode}
                            onChange={(e) => setReportingMode(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            {REPORTING_MODES.map(mode => (
                              <option key={mode.value} value={mode.value}>
                                {mode.label} - {mode.description}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Save Button */}
                        <button
                          onClick={handleSaveScope}
                          disabled={saving || selectedOutlets.length === 0}
                          className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          data-testid="save-scope-btn"
                        >
                          {saving ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : (
                            <Check size={18} />
                          )}
                          Save Scope Assignment
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Application Access Section */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('modules')}
                      className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <Briefcase size={18} className="text-purple-600" />
                        <span className="font-medium">Application Access</span>
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          {applications.length} apps
                        </span>
                      </div>
                      {expandedSections.modules ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    
                    {expandedSections.modules && (
                      <div className="p-4 space-y-3" data-testid="application-access-list">
                        {applications.length === 0 ? (
                          <p className="text-sm text-gray-500 italic" data-testid="application-access-empty">
                            No applications available for this organisation
                          </p>
                        ) : applications.map(app => {
                          const state = ACCESS_STATES[app.effective_access] || ACCESS_STATES.NO_ROLE_ACCESS;
                          const allowed = app.explicit_override !== false;
                          const busy = togglingApp === app.app_code;
                          return (
                            <div 
                              key={app.app_code}
                              className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                              data-testid={`application-row-${app.app_code}`}
                            >
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-sm">{app.display_name}</span>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded ${state.className}`}
                                    data-testid={`application-state-${app.app_code}`}
                                  >
                                    {state.label}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{app.reason}</p>
                                {app.legacy_app_codes?.length > 0 && (
                                  <p className="text-[11px] text-gray-400 mt-0.5">
                                    Legacy alias: {app.legacy_app_codes.join(', ')}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => handleAppToggle(app, !allowed)}
                                disabled={busy}
                                className={`relative shrink-0 w-12 h-6 rounded-full transition-colors disabled:opacity-50 ${
                                  !allowed ? 'bg-gray-300'
                                    : app.effective_access === 'ACTIVE' ? 'bg-green-500'
                                    : 'bg-amber-300'
                                }`}
                                data-testid={`module-toggle-${app.app_code}`}
                                aria-label={`${allowed ? 'Block' : 'Allow'} ${app.display_name}`}
                                title={allowed && app.effective_access !== 'ACTIVE'
                                  ? 'Allowed, but currently inactive' : undefined}
                              >
                                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                  allowed ? 'left-7' : 'left-1'
                                }`} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Capabilities Section (Inspection) */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('capabilities')}
                      className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <Shield size={18} className="text-green-600" />
                        <span className="font-medium">Capabilities (People Engine)</span>
                        {resolvedAccess && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            {resolvedAccess.capabilities?.length || 0} granted
                          </span>
                        )}
                      </div>
                      {expandedSections.capabilities ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    
                    {expandedSections.capabilities && (
                      <div className="p-4">
                        {resolvedAccess?.capabilities?.length > 0 ? (
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            {resolvedAccess.capabilities.map(cap => (
                              <div 
                                key={cap}
                                className="flex items-center gap-2 text-sm py-1"
                              >
                                <Check size={14} className="text-green-600" />
                                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{cap}</code>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No capabilities resolved</p>
                        )}
                        
                        {/* Constraints Summary */}
                        {resolvedAccess?.constraints && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Visibility Constraints</h4>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className={`p-2 rounded ${resolvedAccess.constraints.can_view_payroll ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                                {resolvedAccess.constraints.can_view_payroll ? '✓' : '✗'} Payroll
                              </div>
                              <div className={`p-2 rounded ${resolvedAccess.constraints.can_view_bank_details ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                                {resolvedAccess.constraints.can_view_bank_details ? '✓' : '✗'} Bank Details
                              </div>
                              <div className={`p-2 rounded ${resolvedAccess.constraints.can_view_pan ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                                {resolvedAccess.constraints.can_view_pan ? '✓' : '✗'} PAN
                              </div>
                              <div className={`p-2 rounded ${resolvedAccess.constraints.can_view_salary ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                                {resolvedAccess.constraints.can_view_salary ? '✓' : '✗'} Salary
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AccessAssignmentPanel;
